/**
 * Custom service worker registration with graceful error handling.
 *
 * Service worker registration can fail on certain devices/browsers:
 * - Android WebViews (in-app browsers)
 * - Private/Incognito mode
 * - Low storage devices
 * - Browsers with aggressive battery optimization
 *
 * We handle these cases gracefully without throwing unhandled rejections.
 */

function isWebView(): boolean {
  const ua = navigator.userAgent || "";

  // Common WebView indicators
  const webViewIndicators = [
    // Android WebViews
    "wv)", // Android WebView marker
    "WebView", // Generic WebView

    // Social media in-app browsers
    "FBAN", // Facebook Android
    "FBAV", // Facebook iOS
    "FB_IAB", // Facebook In-App Browser
    "Instagram", // Instagram
    "Twitter", // Twitter/X
    "Line/", // Line
    "KAKAOTALK", // KakaoTalk
    "Snapchat", // Snapchat
    "Pinterest", // Pinterest
    "LinkedIn", // LinkedIn

    // Other in-app browsers
    "GSA/", // Google Search App iOS
  ];

  // iOS standalone mode check (added to home screen)
  // This is fine, but WKWebView in apps may have issues
  const isIOSWebView =
    /iPhone|iPad|iPod/.test(ua) && !("standalone" in navigator) && !/Safari/.test(ua);

  return webViewIndicators.some((indicator) => ua.includes(indicator)) || isIOSWebView;
}

export async function registerServiceWorker(): Promise<void> {
  // Skip if service workers aren't supported
  if (!("serviceWorker" in navigator)) {
    return;
  }

  // Skip in WebViews - they have limited/buggy SW support
  if (isWebView()) {
    console.log("Skipping service worker registration in WebView");
    return;
  }

  // Wait for the page to load
  if (document.readyState !== "complete") {
    await new Promise<void>((resolve) => {
      window.addEventListener("load", () => resolve(), { once: true });
    });
  }

  try {
    // Append build timestamp to bypass browser cache
    const swUrl = `/sw.js?v=${BUILD_TIMESTAMP}`;
    const registration = await navigator.serviceWorker.register(swUrl, {
      scope: "/",
    });

    console.log("Service worker registered:", registration.scope);
  } catch (error) {
    // Silently handle registration failures - the app works fine without SW
    // Common failures: storage quota, WebView restrictions, incognito mode
    console.log(
      "Service worker registration skipped:",
      error instanceof Error ? error.message : "unknown error",
    );
  }
}
